import { Fragment, memo, useCallback, useEffect, useState } from "react";
import {
  faChevronDown,
  faChevronUp,
  faCircleInfo,
} from "@fortawesome/pro-regular-svg-icons";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { Routine } from "@/app/routines/routines";
import { intensityToRIR } from "@/app/training/rir";
import { TrainingCategory } from "@/app/training/training-category";
import { TrainingDay } from "@/app/training/training-day";
import { CheckBox } from "@/shared/components/atoms/CheckBox";
import { InputTextRightWhite } from "@/shared/components/atoms/InputTextRightWhite";
import { InputTextWhite } from "@/shared/components/atoms/InputTextWhite";
import EllipsesMenuExerciseWeeks from "@/shared/components/molecules/EllipsesMenuExerciseWeeks";
import {
  TrainingSection,
  TrainingValues,
} from "@/shared/schemas/training.schema";
import { TableLoader } from "../molecules/TableLoader";
import { faPlayCircle } from "@fortawesome/free-solid-svg-icons";
import { ExercisePreview } from "./ExercisePreview";
import { Modal } from "./Modal";
import { FormikProps } from "formik";
import { calculatedEstimatedTime } from "../utils/calculateEstimatedTime";
import { GripVertical } from "lucide-react";

export type RoutineEventPayload = {
  day: TrainingDay;
  category: TrainingCategory;
  index: number;
};

export type RoutineEventHandler = (payload: {
  day: TrainingDay;
  category: TrainingCategory;
  index: number;
  exercise?: any;
}) => void | any;

export type TableListExercisesWeekProps = {
  onChange: FormikProps<TrainingValues>["handleChange"];
  onBlur: FormikProps<TrainingValues>["handleBlur"];
  onClickViewExercise: (routine: Routine) => void;
  onMoveUpRoutine: RoutineEventHandler;
  onMoveDownRoutine: RoutineEventHandler;
  onRemoveRoutine: RoutineEventHandler;
  onChangeRoutineExercise: RoutineEventHandler;
  onAddComment: ({ message, index, category, day }: any) => void;
  isLoading: boolean;
  title: string;
  rir?: boolean;
  section: TrainingSection;
  day: TrainingDay;
  category: TrainingCategory;
  onDropExercise?: (exercise: any) => void;
  validationErrors?: {
    day: TrainingDay | null;
    category: TrainingCategory | null;
    index: number | null;
    field: string | null;
  };
};

type TimeResult = {
  tiempo: string;
  descanso: number;
  serie: number;
  typeCalculated: string;
};

const SortableRoutineItem: React.FC<{
  routine: any;
  index: number;
  day: TrainingDay;
  category: TrainingCategory;
  onChange: FormikProps<TrainingValues>["handleChange"];
  onBlur: FormikProps<TrainingValues>["handleBlur"];
  onClickViewExercise: (routine: Routine) => void;
  onMoveUpRoutine: RoutineEventHandler;
  onMoveDownRoutine: RoutineEventHandler;
  onRemoveRoutine: RoutineEventHandler;
  onChangeRoutineExercise: RoutineEventHandler;
  onAddComment: ({ message, index, category, day }: any) => void;
  sectionLength: number;
  openModal: (item: any) => void;
  setIsModalOpen: (open: boolean) => void;
  setSelectItem: (item: any) => void;
  handleAddEvolution: (routine: any) => void;
  setComentario: React.Dispatch<
    React.SetStateAction<{
      index: number;
      state: boolean;
      comentario: string;
    }>
  >;
  spanRight?: string;
  validationErrors?: {
    day: TrainingDay | null;
    category: TrainingCategory | null;
    index: number | null;
    field: string | null;
  };
}> = memo(
  ({
    routine,
    index,
    day,
    category,
    onChange,
    onBlur,
    onClickViewExercise,
    onMoveUpRoutine,
    onMoveDownRoutine,
    onRemoveRoutine,
    onChangeRoutineExercise,
    onAddComment,
    spanRight,
    sectionLength,
    openModal,
    setIsModalOpen,
    setSelectItem,
    setComentario,
    validationErrors,
    handleAddEvolution,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: routine.id || `${day}-${category}-${index}`,
      });

    const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false);

    const converterEvolutions = (evolutions: string) => {
      if (!evolutions || evolutions === "") {
        return [];
      }
      const evolutionsArray = evolutions.split(",");
      return evolutionsArray.map((weight, index) => ({
        series: index + 1,
        weight: Number(weight),
      }));
    };

    const totalSeries = routine?.serie || 0;

    const evolutionData = converterEvolutions(routine?.evolutions);

    // Inicializa weights con valores de evolutions si existen
    const initialWeights = Array(totalSeries).fill("");
    evolutionData.forEach((evo) => {
      if (evo.series <= totalSeries) {
        initialWeights[evo.series - 1] = evo.weight.toString();
      }
    });

    const [weightInputs, setWeightInputs] = useState<string[]>(initialWeights);
    const repetitionsArray = Array.from(
      { length: totalSeries },
      (_, i) => i + 1
    );

    // Maneja el cambio de cada input
    const handleWeightInputChange = (index: number, value: string) => {
      const newWeights = [...weightInputs];
      newWeights[index] = value;
      setWeightInputs(newWeights);
    };

    const handleSaveWeights = () => {
      const newEvolutions = weightInputs
        .filter((weight) => weight !== "") // Solo incluir pesos no vacíos
        .join(",");
      if (newEvolutions) {
        handleAddEvolution({
          evolution: newEvolutions,
          index,
          category,
          day,
        });
      }
      setIsSeriesModalOpen(false);
    };
    /*     const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    }; */

    // o si en al guna categoriia no hay ni un ejercisi
    const isErrorField = (field: string) =>
      validationErrors?.day === day &&
      validationErrors?.category === category &&
      validationErrors?.index === index &&
      validationErrors?.field === field;

    const rirText =
      category === "main" ? intensityToRIR(routine?.intensity) : "";

    return (
      <>
      <tr className="flex flex-col md:table-row border-b md:border-b-0">
        <td className="py-2 px-3 text-sm md:table-cell">
          <div className="flex items-center w-full md:flex-row flex-col">
            <div
              className="text-gray-400 mr-2 md:mb-0 mb-2"
              {...attributes}
              {...listeners}
            ></div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClickViewExercise(routine);
              }}
              className="flex-grow md:w-auto w-full"
            >
              <div
                className="relative overflow-hidden flex items-center justify-center w-20 h-20"
                onClick={() => {
                  openModal({
                    url: routine?.exercise?.video_url,
                    index: index,
                    break: routine?.break,
                    serie: routine?.serie,
                    superSerie: routine?.superSerie,
                    repetitions: routine?.repetitions,
                    intensity: routine?.intensity,
                    name: routine?.exercise?.exercise,
                    review: routine?.exercise?.review,
                    second_main_muscle: routine?.exercise?.second_main_muscle,
                    second_secondary_muscle:
                      routine?.exercise?.second_secondary_muscle,
                    img:
                      routine?.exercise?.personalized_img ||
                      routine?.exercise?.image_url,
                    first_alternative: routine?.exercise?.first_alternative,
                  });
                }}
              >
                <img
                  src={
                    routine?.exercise?.personalized_img ||
                    routine?.exercise?.imageUrl ||
                    routine?.exercise?.image_url
                  }
                  className="h-16 w-16 rounded-md mx-auto"
                  alt="avatar"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-full">
                  <FontAwesomeIcon
                    icon={faPlayCircle}
                    className="text-white"
                  />
                </div>
              </div>
            </button>
          </div>
          <span className="md:hidden text-sm font-semibold block mt-2">
            {t("trainingEditor.table.exercise")}
          </span>
        </td>
        <td className="py-2 text-sm md:table-cell">
          <div className="md:flex md:items-center md:justify-between flex-col">
            <span className="md:hidden text-sm font-semibold block mb-1">
              {t("trainingEditor.table.series")}
            </span>
            <InputTextWhite
              label=""
              name={`training.${day}.${category}.routine.${index}.serie`}
              value={routine.serie}
              onChange={onChange}
              onBlur={onBlur}
              className={`w-full ${isErrorField("serie") ? "border-red-500 border-2 animate-pulse" : !routine.serie || routine?.serie?.trim?.() === "" ? "border-red-500 border-2" : ""}`}
            />
          </div>
        </td>
        <td className="py-2 text-sm md:table-cell">
          <div className="md:flex md:items-center md:justify-between flex-col">
            <span className="md:hidden text-sm font-semibold block mb-1">
              {t("trainingEditor.table.repetitions_time")}
            </span>
            <InputTextRightWhite
              label=""
              spanRight={spanRight}
              name={`training.${day}.${category}.routine.${index}.repetitions`}
              value={routine?.repetitions?.replace(";", ",")}
              onChange={onChange}
              onBlur={onBlur}
              className={`w-full ${isErrorField("repetitions") ? "border-red-500 border-2 animate-pulse" : !routine?.repetitions || routine?.repetitions === "" || routine?.repetitions < 1 ? "border-red-500 border-2" : ""}`}
            />
          </div>
        </td>
        <td className="py-2 text-sm md:table-cell">
          <div className="flex-col md:flex md:flex-row md:items-center">
            <span className="md:hidden text-sm font-semibold block mb-1">
              {t("trainingEditor.table.intensity")}
            </span>
            <div className="flex items-center w-full">
              <div className="md:w-2/2 w-full">
                <InputTextRightWhite
                  label=""
                  spanRight="%"
                  name={`training.${day}.${category}.routine.${index}.intensity`}
                  value={routine?.intensity}
                  onChange={onChange}
                  onBlur={onBlur}
                  className={`w-full ${isErrorField("intensity") ? "border-red-500 border-2 animate-pulse" : category !== "stretching" && (!routine?.intensity || routine?.intensity === "" || routine?.intensity < 1) ? "border-red-500 border-2" : ""}`}
                />
              </div>
              <div className="md:w-1/3 w-full md:pl-2 md:mt-0 mt-1">
                <span className="text-xs text-gray-500 block whitespace-nowrap">
                  {rirText || ""}
                </span>
              </div>
            </div>
          </div>
          </td>
          <td className="py-2 text-sm md:table-cell">
            <div className="flex flex-col items-center md:flex-row md:justify-center">
              <span className="md:hidden text-sm font-semibold mb-1">
                {t("trainingEditor.table.break_seconds")}
              </span>
              <InputTextRightWhite
                label=""
                spanRight="seg"
                name={`training.${day}.${category}.routine.${index}.break`}
                value={routine?.superSerie ? "0" : routine?.break}
                onChange={onChange}
                onBlur={onBlur}
                className={`w-full ${isErrorField("break") ? "border-red-500 border-2 animate-pulse" : !routine?.break || routine?.break === "" || routine?.break < 1 ? (routine?.superSerie ? "border-primary" : "border-red-500 border-2") : ""}`}
                disabled={routine?.superSerie}
              />
            </div>
          </td>
          <td className="py-2 text-sm md:table-cell">
            <div className="flex flex-col items-center md:flex-row md:justify-center">
              <span className="md:hidden text-sm font-semibold mb-1">
                {t("trainingEditor.table.super_serie")}
              </span>
              <CheckBox
                name={`training.${day}.${category}.routine.${index}.superSerie`}
                value={routine?.superSerie}
                onChange={onChange}
                disabled={index + 1 === sectionLength}
              />
            </div>
          </td>
          <td className="py-2 text-sm md:table-cell">
            <div className="md:flex md:items-center md:justify-between flex-col">
              <span className="md:hidden text-sm font-semibold block mb-1">
                {t("trainingEditor.table.order")}
              </span>
              <div className="flex flex-col items-center gap-2">
                <button
                  disabled={index === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveUpRoutine({ day, category, index });
                  }}
                >
                  <FontAwesomeIcon icon={faChevronUp} />
                </button>
                <button
                  disabled={index === sectionLength - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveDownRoutine({ day, category, index });
                  }}
                >
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
              </div>
            </div>
          </td>
          <td className="text-sm md:table-cell items-center flex justify-center">
            <div className="flex items-center justify-center w-full">
              <EllipsesMenuExerciseWeeks
                onChangeExercise={() => {
                  setIsModalOpen(true);
                  setSelectItem({
                    id: routine?.exercise?.id,
                    index: index,
                    url:
                      routine?.exercise?.video_url ||
                      routine?.exercise?.videoUrl,
                    name: routine?.exercise?.exercise,
                    review: routine?.exercise?.review,
                    second_main_muscle: routine?.exercise?.second_main_muscle,
                    first_alternative: routine?.exercise?.first_alternative,
                    second_secondary_muscle:
                      routine?.exercise?.second_secondary_muscle,
                    img:
                      routine?.exercise?.personalized_img ||
                      routine?.exercise?.image_url ||
                      routine?.exercise?.imageUrl,
                  });
                }}
                onWeight={(e) => {
                  e.stopPropagation();
                  setIsSeriesModalOpen(true);
                }}
                activeWeight={category?.toLowerCase?.() === "main"}
                onDelete={() => onRemoveRoutine({ day, category, index })}
                onComment={() =>
                  setComentario({
                    index: index,
                    state: true,
                    comentario: routine?.commentary_custom || "",
                  })
                }
              />
            </div>
          </td>
        </tr>

        {/* Weights Modal */}
        <Modal
          open={isSeriesModalOpen}
          Close={() => {
            handleSaveWeights();
            setIsSeriesModalOpen(false);
          }}
          onClickCreate={() => {
            handleSaveWeights();
          }}
          tittleModal={t("trainingEditor.table.modifyWeights")}
          textButtonCreate={t("trainingEditor.table.save")}
          textButtonClose={t("trainingEditor.table.cancel")}
        >
          {category === "main" && (
            <div className="flex flex-col gap-4 justify-center">
              {repetitionsArray?.map((rep, idx) => (
                <InputTextRightWhite
                  key={idx}
                  type="number"
                  label={`${t("trainingEditor.table.series")} ${idx + 1}`}
                  value={weightInputs[idx] || ""}
                  onChange={(e) => handleWeightInputChange(idx, e.target.value)}
                  className="w-full"
                  spanRight={spanRight ?? "kg"}
                />
              ))}
            </div>
          )}
        </Modal>
      </>
    );
  }
);

export const RoutineTable: React.FC<TableListExercisesWeekProps> = memo(
  ({
    onMoveUpRoutine,
    onMoveDownRoutine,
    onRemoveRoutine,
    onAddComment,
    onChangeRoutineExercise,
    title,
    day,
    category,
    section,
    isLoading,
    onClickViewExercise,
    onChange,
    onBlur,
    onDropExercise,
    handleAddEvolution,
    validationErrors,
    exerciseLevanted,
    spanRight,
  }: any) => {
    const { setNodeRef, isOver } = useDroppable({
      id: `${day}-${category}-droppable`,
      data: { day, category },
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [item, setSelectItem] = useState<any>({});
    const [time, setTime] = useState<number | undefined>();
    const [comentario, setComentario] = useState({
      index: 0,
      state: false,
      comentario: "",
    });

    const openModal = useCallback((item: any) => {
      setSelectItem(item);
      setIsModalOpen(true);
    }, []);

    const exampleRows = () => {
      const rows: TimeResult[] = [];
      section?.routine.forEach((routine: any) => {
        const timeResult: TimeResult = {
          tiempo: routine?.repetitions,
          descanso: routine?.break,
          serie: routine?.serie,
          typeCalculated: routine?.exercise?.um,
        };
        rows.push(timeResult);
      });
      return rows;
    };

    const estimateTime = () => {
      const rows: any = exampleRows();
      const time = calculatedEstimatedTime(rows);
      setTime(time);
      return time;
    };

    let init = true;
    useEffect(() => {
      if (init) {
        init = false;
        estimateTime();
      }
    }, [section?.routine]);

    const changeExercise = ({ newExercise }: any) => {
      const currentExercise = section?.routine[item?.index];
      onChangeRoutineExercise &&
        onChangeRoutineExercise({
          day,
          category,
          index: item?.index,
          exercise: {
            day,
            category,
            exercise: {
              ...newExercise,
              image_url:
                newExercise?.personalized_img ||
                newExercise?.imageUrl ||
                newExercise?.image_url,
              video_url:
                newExercise?.personalized_video ||
                newExercise?.videoUrl ||
                newExercise?.video_url,
            },
            break: currentExercise?.break,
            serie: currentExercise?.serie,
            superSerie: currentExercise?.superSerie,
            repetitions: currentExercise?.repetitions,
            intensity: currentExercise?.intensity,
            //@ts-ignore
            muscle: currentExercise?.first_main_muscle,
          },
        });
      setSelectItem(null);
    };

    const items = section?.routine.map(
      (routine, index) => routine.id || `${day}-${category}-${index}`
    );

    return (
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {/*      <div
          ref={setNodeRef}
          className="px-2 mt-5 items-center relative"
          style={{
            backgroundColor: isOver ? "#e0f7fa" : "transparent",
            border: isOver ? "2px dashed #38bdf8" : "none",
            borderRadius: "8px",
          }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between mb-4  p-y-2">
            <h1 className="text-base font-semibold leading-6 text-gray-900 mt-0 md:mt-0">
              {title}
            </h1>
            <div className=" flex items-center space-x-4">
              <div className="">
                <h3>{time ?? 0}</h3>
              </div>
              <a
                className="text-primary cursor-pointer md:ml-4"
                onClick={() => estimateTime()}
              >
                {t("user.views.estimated")}
              </a>
            </div>
          </div>

          {isLoading ? (
            <TableLoader columns={7} rows={3} />
          ) : (
            <Fragment>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y items-center  divide-gray-300">
                  <thead className="hidden md:table-header-group ml-20 pl-10">
                    <tr className="items-center ml-20 pl-10 w-full">
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900"></th>
                      <th className="ml-20 px-3 text-left text-sm font-semibold text-gray-900">
                        {t("user.views.series")}
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          {t("user.views.repetitions_time")}
                          <div className="group ml-2">
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              className="text-gray-400 cursor-pointer"
                            />
                            <div className="absolute hidden group-hover:block mt-2 w-56 p-5 bg-white shadow-lg rounded-lg text-gray-700 text-sm z-10">
                              {t("training-wizard.views.second_information")}
                            </div>
                          </div>
                        </div>
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        {t("user.views.intensity")}
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                        {t("trainingEditor.table.break_seconds")}
                          <div className="group ml-2">
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              className="text-gray-400 cursor-pointer"
                            />
                            <div className="absolute hidden group-hover:block mt-2 w-56 p-5 bg-white shadow-lg rounded-lg text-gray-700 text-sm z-10">
                              {t("training-wizard.views.second_information")}
                            </div>
                          </div>
                        </div>
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          {t("user.views.super_serie")}
                          <div className="group ml-2">
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              className="text-gray-400 cursor-pointer"
                            />
                            <div className="absolute hidden group-hover:block mt-2 w-56 p-5 bg-white shadow-lg rounded-lg text-gray-700 text-sm z-10">
                              {t(
                                "training-wizard.views.super_serie_information"
                              )}
                            </div>
                          </div>
                        </div>
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        {t("user.views.order")}
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white mr-20">
                    {section?.routine.map((routine: any, index) => (
                      <SortableRoutineItem
                        handleAddEvolution={handleAddEvolution}
                        key={routine.id || `${day}-${category}-${index}`}
                        routine={routine}
                        index={index}
                        day={day}
                        category={category}
                        onChange={onChange}
                        onBlur={onBlur}
                        onClickViewExercise={onClickViewExercise}
                        onMoveUpRoutine={onMoveUpRoutine}
                        onMoveDownRoutine={onMoveDownRoutine}
                        onRemoveRoutine={onRemoveRoutine}
                        onChangeRoutineExercise={onChangeRoutineExercise}
                        onAddComment={onAddComment}
                        sectionLength={section.routine.length}
                        openModal={openModal}
                        setIsModalOpen={setIsModalOpen}
                        setSelectItem={setSelectItem}
                        setComentario={setComentario}
                        validationErrors={validationErrors}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Fragment>
          )}

          {isModalOpen && (
            <ExercisePreview
              onSelectChange={changeExercise}
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              data={item}
            />
          )}

          <Modal
            open={comentario.state}
            Close={() =>
              setComentario({ state: false, comentario: "", index: 0 })
            }
            onClickCreate={() => {
              onAddComment({
                message: comentario.comentario,
                day: day,
                category: category,
                index: comentario.index,
              });
              setComentario({ state: false, comentario: "", index: 0 });
            }}
            tittleModal="Comentario"
            textButtonCreate="Guardar"
            textButtonClose="Cancelar"
          >
            <div className="flex flex-col gap-4">
              <textarea
                className="border p-2 rounded w-full"
                value={comentario.comentario}
                onChange={(e) =>
                  setComentario({ ...comentario, comentario: e.target.value })
                }
                placeholder="Escribe tu comentario aquí..."
              />
            </div>
          </Modal>
        </div> */}

        <div
          ref={setNodeRef}
          className="px-2 mt-5 items-center relative"
          style={{
            backgroundColor: isOver ? "#e0f7fa" : "transparent",
            border: isOver ? "2px dashed #38bdf8" : "none",
            borderRadius: "8px",
          }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between mb-4 p-y-2">
            <h1 className="text-base font-semibold leading-6 text-gray-900 mt-0 md:mt-0">
              {title}
            </h1>
            <div className="flex items-center space-x-4">
              <div className="flex row gap-2">
                <h3>{time ?? 0}</h3> min
              </div>
              <a
                className="text-primary cursor-pointer md:ml-4"
                onClick={() => estimateTime()}
              >
                {t("trainingEditor.table.estimated")}
              </a>
            </div>
          </div>

          {isLoading ? (
            <TableLoader columns={7} rows={3} />
          ) : (
            <Fragment>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y items-center divide-gray-300">
                  <thead className="hidden md:table-header-group ml-20 pl-10">
                    <tr className="items-center ml-20 pl-10 w-full">
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900"></th>
                      <th className="ml-20 px-3 text-left text-sm font-semibold text-gray-900">
                        {t("trainingEditor.table.series")}
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          {t("trainingEditor.table.repetitions_time")}
                          <div className="group ml-2">
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              className="text-gray-400 cursor-pointer"
                            />
                            <div className="absolute hidden group-hover:block mt-2 w-56 p-5 bg-white shadow-lg rounded-lg text-gray-700 text-sm z-10">
                              {t("trainingEditor.table.second_information")}
                            </div>
                          </div>
                        </div>
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        {t("trainingEditor.table.intensity")}
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          {t("trainingEditor.table.break_seconds")}
                          <div className="group ml-2">
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              className="text-gray-400 cursor-pointer"
                            />
                            <div className="absolute hidden group-hover:block mt-2 w-56 p-5 bg-white shadow-lg rounded-lg text-gray-700 text-sm z-10">
                              {t("trainingEditor.table.second_information")}
                            </div>
                          </div>
                        </div>
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          {t("trainingEditor.table.super_serie")}
                          <div className="group ml-2">
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              className="text-gray-400 cursor-pointer"
                            />
                            <div className="absolute hidden group-hover:block mt-2 w-56 p-5 bg-white shadow-lg rounded-lg text-gray-700 text-sm z-10">
                              {t(
                                "trainingEditor.table.super_serie_information"
                              )}
                            </div>
                          </div>
                        </div>
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                        {t("trainingEditor.table.order")}
                      </th>
                      <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white mr-20">
                    {section?.routine.map((routine, index) => (
                      <SortableRoutineItem
                        handleAddEvolution={handleAddEvolution}
                        key={routine.id || `${day}-${category}-${index}`}
                        routine={routine}
                        index={index}
                        spanRight={spanRight}
                        day={day}
                        category={category}
                        onChange={onChange}
                        onBlur={onBlur}
                        onClickViewExercise={onClickViewExercise}
                        onMoveUpRoutine={onMoveUpRoutine}
                        onMoveDownRoutine={onMoveDownRoutine}
                        onRemoveRoutine={onRemoveRoutine}
                        onChangeRoutineExercise={onChangeRoutineExercise}
                        onAddComment={onAddComment}
                        sectionLength={section.routine.length}
                        openModal={openModal}
                        setIsModalOpen={setIsModalOpen}
                        setSelectItem={setSelectItem}
                        setComentario={setComentario}
                        validationErrors={validationErrors}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Fragment>
          )}

          {isModalOpen && (
            <ExercisePreview
              onSelectChange={changeExercise}
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              data={item}
            />
          )}

          <Modal
            open={comentario.state}
            Close={() =>
              setComentario({ state: false, comentario: "", index: 0 })
            }
            onClickCreate={() => {
              onAddComment({
                message: comentario.comentario,
                day: day,
                category: category,
                index: comentario.index,
              });
              setComentario({ state: false, comentario: "", index: 0 });
            }}
            tittleModal={t("trainingEditor.table.comment")}
            textButtonCreate={t("trainingEditor.table.save")}
            textButtonClose={t("trainingEditor.table.cancel")}
          >
            <div className="flex flex-col gap-4">
              <textarea
                className="border p-2 rounded w-full"
                value={comentario.comentario}
                onChange={(e) =>
                  setComentario({ ...comentario, comentario: e.target.value })
                }
                placeholder={t(
                  "trainingEditor.table.write_comment_placeholder"
                )}
              />
            </div>
          </Modal>
        </div>
      </SortableContext>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.section === nextProps.section &&
      prevProps.day === nextProps.day &&
      prevProps.category === nextProps.category &&
      prevProps.title === nextProps.title &&
      prevProps.validationErrors === nextProps.validationErrors
    );
  }
);

export default RoutineTable;
